import type { Geometry } from 'geojson'
import type { SearchResultItem } from '@onxmaps/search-web'
import type { GeoJSONFeature } from '@/lib/map-x/webmap-viewer-types'
import Geographic from '@/lib/geographic'
import pointOnFeature from '@turf/point-on-feature'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>

interface MapFeatureData {
  id: string
  label: string
  presentation: AnyRecord
  source?: string
  layer: string
  type?: string
  layerType?: string
  properties?: AnyRecord
  geometry?: Geometry | null
  [key: string]: unknown
}

/**
 * Normalizes geometry types between OpenLayers and Mapbox. Converts to GeoJSON standard
 * @param type Type to convert
 * @returns Normalized type
 */
function normalizeType(type: string | undefined): string | undefined {
  switch (type) {
    case 'symbol':
      return 'Point'

    case 'line':
      return 'LineString'

    case 'fill':
    case 'raster':
      return 'Polygon'

    default:
      return type
  }
}

/**
 * Synthetic layer name for features derived from a search result rather than a
 * map tile. The query card pins these to the top of its feature list.
 */
export const SEARCH_FEATURE_LAYER = 'search'

/**
 *  Generic Map Feature Interface
 */
export default class MapFeature {
  id: string
  label: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  presentation: AnyRecord
  source: string
  layer: string
  type: string
  layerType: string
  sourceLayer: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: AnyRecord
  geometry: Geometry | null | undefined

  /**
   * @constructor
   */
  constructor(feature: MapFeatureData) {
    this.id = feature.id
    this.label = feature.label
    this.presentation = feature.presentation
    this.source = feature.source ?? ''
    this.layer = feature.layer
    this.type = feature.type ?? ''
    this.layerType = feature.layerType ?? ''
    this.sourceLayer = this.layer
    this.properties = feature.properties ?? {}
    this.geometry = feature.geometry
  }

  /**
   * Gets first point on geometry handles multiline strings
   * @returns Point
   */
  getPointOnFeature(): Geographic {
    const {
      geometry: { coordinates },
    } = pointOnFeature(this as GeoJSON.Feature)
    return Geographic.fromTurfPoint(coordinates)
  }

  /**
   * Converts Mapbox feature to generic feature
   * @param feature - Mapbox feature
   * @param useGeometryType - Should use geometry type instead of layer type
   * @returns Generic Map Feature
   */
  static fromMapbox(
    feature: GeoJSONFeature,
    useGeometryType = false
  ): MapFeature {
    const presentation = feature.properties?.presentation
      ? JSON.parse(feature.properties.presentation as string)
      : {}

    return new MapFeature({
      id: String(feature.id ?? feature.properties?.id ?? ''),
      label: (feature.properties?.label as string) ?? '',
      presentation,
      source: feature.source,
      layer: feature.sourceLayer ?? '',
      type: feature.type,
      layerType: normalizeType(
        useGeometryType ? feature.geometry?.type : feature.layer?.type
      ),
      properties: {
        ...presentation,
        ...feature.properties,
      },
      geometry: feature.geometry,
    })
  }

  /**
   * Converts Search service feature to generic feature
   * @param feature - Search result feature
   * @returns Generic Map Feature
   */
  static fromSearch(feature: SearchResultItem): MapFeature {
    const presentation: Record<string, string>[] = [
      { Name: feature.item.name },
      { County: feature.countyName ?? '' },
      { State: feature.state?.name ?? '' },
      { Nearby: feature.nearby ?? '' },
    ]
    const hardcodedKeys = ['Name', 'County', 'State', 'Nearby']
    const isValidExtra = (extra: string | null | undefined) =>
      extra && extra !== '0' && extra !== 'null' && extra !== ''
    // Pull additional features out of presentation block
    // Don't display empty or duplicated info
    feature.item?.presentation?.forEach((item) => {
      if (
        item.label &&
        isValidExtra(item.label) &&
        !hardcodedKeys.includes(item.label)
      ) {
        presentation.push({ [item.label]: item.value ?? '' })
      }
    })
    const scopeName = feature.item.searchScopes?.[0]?.name ?? ''
    return new MapFeature({
      id: feature.item.id,
      label: feature.item.name,
      title: scopeName,
      presentation: {
        content: [presentation],
        title: scopeName,
      },
      layer: SEARCH_FEATURE_LAYER,
      type: feature.item.geometry?.type,
      layerType: feature.item.geometry?.type,
      geometry: feature.item.geometry,
    })
  }
}
